/**
 * Chart.js initialization for visa bulletin trend pages
 * Handles rendering of filing dates and final action dates charts
 */

/**
 * Custom Chart.js plugin to draw vertical lines for October months
 */
const octoberLinesPlugin = {
  id: "octoberLines",
  beforeDatasetsDraw(chart, args, options) {
    const {
      ctx,
      chartArea: { top, bottom },
      scales: { x },
    } = chart;

    if (!options.octoberIndices || options.octoberIndices.length === 0) {
      return;
    }

    ctx.save();
    options.octoberIndices.forEach((index) => {
      const xPos = x.getPixelForValue(index);
      ctx.strokeStyle = "rgba(64, 64, 64, 0.4)";
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(xPos, top);
      ctx.lineTo(xPos, bottom);
      ctx.stroke();
    });
    ctx.restore();
  },
};

/**
 * Initialize combined chart for filing dates and final action dates on category pages
 * @param {Object} chartData - The chart data object containing filing_dates and final_action_dates
 */
export function initializeCharts(chartData) {
  try {
    if (!chartData) {
      console.error("Chart data is missing");
      showChartError("combined-category-chart", "No data available for chart");
      return;
    }

    if (!chartData.filing_dates || !chartData.final_action_dates) {
      console.error("Missing required chart data");
      showChartError("combined-category-chart", "Chart data is incomplete");
      return;
    }

    // Initialize combined category chart
    initializeCombinedCategoryChart(chartData);
  } catch (error) {
    console.error("Error initializing charts:", error);
    showChartError("combined-category-chart", "Failed to initialize chart");
  }
}

/**
 * Initialize combined chart for category pages showing both filing and final action dates
 * @param {Object} chartData - The chart data object containing filing_dates and final_action_dates
 */
function initializeCombinedCategoryChart(chartData) {
  try {
    const canvas = document.getElementById("combined-category-chart");
    if (!canvas) {
      console.error("Combined category chart canvas not found");
      return;
    }

    // Remove loading state if present
    const wrapper = canvas.closest(".chart-wrapper");
    if (wrapper) {
      wrapper.classList.remove("loading");
      const loadingDiv = wrapper.querySelector(".chart-loading");
      if (loadingDiv) {
        loadingDiv.remove();
      }
    }

    const ctx = canvas.getContext("2d");

    // Make canvas keyboard accessible
    canvas.setAttribute("tabindex", "0");

    // Prepare combined datasets from filing_dates and final_action_dates
    const combinedData = prepareCombinedCategoryData(chartData);

    if (!combinedData.labels || combinedData.labels.length === 0) {
      throw new Error("No data available for chart");
    }

    // Collect all data values for min/max calculation
    const allDataValues = combinedData.datasets.flatMap(
      (dataset) => dataset.data,
    );

    // Identify October bulletin months for vertical line markers
    const octoberIndices = combinedData.labels
      .map((label, index) => (label.includes("Oct") ? index : -1))
      .filter((index) => index !== -1);

    // Responsive point sizes based on screen width
    const isSmallScreen = window.innerWidth < 480;
    const pointRadius = isSmallScreen ? 2 : 4;
    const pointHoverRadius = isSmallScreen ? 4 : 6;

    // Apply styling to datasets
    const styledDatasets = combinedData.datasets.map((dataset, index) => ({
      ...dataset,
      tension: 0.1,
      pointRadius: pointRadius,
      pointHoverRadius: pointHoverRadius,
      borderWidth: 2,
    }));

    const chart = new Chart(ctx, {
      type: "line",
      data: {
        labels: combinedData.labels,
        datasets: styledDatasets,
      },
      options: getChartOptions(
        "Filing and Final Action Dates",
        allDataValues,
        octoberIndices,
      ),
      plugins: [octoberLinesPlugin],
    });

    // Add keyboard navigation support
    addKeyboardNavigation(canvas, chart);

    // Add window resize listener for responsive behavior
    addResizeListener(chart);
  } catch (error) {
    console.error("Error creating combined category chart:", error);
    showChartError("combined-category-chart", "Failed to render chart");
  }
}

/**
 * Initialize combined chart for country overview pages showing all categories
 * @param {Object} chartData - Chart data containing datasets for all categories
 */
export function initializeCombinedChart(chartData) {
  try {
    const canvas = document.getElementById("combined-chart");
    if (!canvas) {
      console.error("Combined chart canvas not found");
      return;
    }

    // Remove loading state if present
    const wrapper = canvas.closest(".chart-wrapper");
    if (wrapper) {
      wrapper.classList.remove("loading");
      const loadingDiv = wrapper.querySelector(".chart-loading");
      if (loadingDiv) {
        loadingDiv.remove();
      }
    }

    const ctx = canvas.getContext("2d");

    // Make canvas keyboard accessible
    canvas.setAttribute("tabindex", "0");

    // Validate chart data structure
    if (
      !chartData.labels ||
      !chartData.datasets ||
      chartData.datasets.length === 0
    ) {
      throw new Error("Invalid chart data structure");
    }

    // Collect all data values for min/max calculation
    const allDataValues = chartData.datasets.flatMap((dataset) => dataset.data);

    // Identify October bulletin months for vertical line markers
    const octoberIndices = chartData.labels
      .map((label, index) => (label.includes("Oct") ? index : -1))
      .filter((index) => index !== -1);

    // Responsive point sizes based on screen width
    const isSmallScreen = window.innerWidth < 480;
    const pointRadius = isSmallScreen ? 2 : 4;
    const pointHoverRadius = isSmallScreen ? 4 : 6;

    // Define distinct colors for different categories
    const categoryColors = [
      { border: "rgb(255, 99, 132)", background: "rgba(255, 99, 132, 0.1)" }, // Red
      { border: "rgb(54, 162, 235)", background: "rgba(54, 162, 235, 0.1)" }, // Blue
      { border: "rgb(255, 206, 86)", background: "rgba(255, 206, 86, 0.1)" }, // Yellow
      { border: "rgb(75, 192, 192)", background: "rgba(75, 192, 192, 0.1)" }, // Teal
      { border: "rgb(153, 102, 255)", background: "rgba(153, 102, 255, 0.1)" }, // Purple
      { border: "rgb(255, 159, 64)", background: "rgba(255, 159, 64, 0.1)" }, // Orange
      { border: "rgb(201, 203, 207)", background: "rgba(201, 203, 207, 0.1)" }, // Grey
    ];

    // Apply colors to datasets
    const coloredDatasets = chartData.datasets.map((dataset, index) => ({
      ...dataset,
      borderColor: categoryColors[index % categoryColors.length].border,
      backgroundColor: categoryColors[index % categoryColors.length].background,
      tension: 0.1,
      pointRadius: pointRadius,
      pointHoverRadius: pointHoverRadius,
      borderWidth: 2,
    }));

    const chart = new Chart(ctx, {
      type: "line",
      data: {
        labels: chartData.labels,
        datasets: coloredDatasets,
      },
      options: getChartOptions("All Categories", allDataValues, octoberIndices),
      plugins: [octoberLinesPlugin],
    });

    // Add keyboard navigation support
    addKeyboardNavigation(canvas, chart);

    // Add window resize listener for responsive behavior
    addResizeListener(chart);
  } catch (error) {
    console.error("Error creating combined chart:", error);
    showChartError("combined-chart", "Failed to render chart");
  }
}

/**
 * Prepare combined data for category charts showing both filing and final action dates
 * @param {Object} chartData - Chart data containing filing_dates and final_action_dates
 * @returns {Object} Combined chart data structure
 */
function prepareCombinedCategoryData(chartData) {
  const filingDates = chartData.filing_dates || { labels: [], datasets: [] };
  const finalActionDates = chartData.final_action_dates || {
    labels: [],
    datasets: [],
  };

  // Use filing dates labels as the base (they should be the same)
  const labels = filingDates.labels || [];

  // Create datasets for both filing and final action dates
  const datasets = [];

  // Add filing dates dataset
  if (filingDates.datasets && filingDates.datasets.length > 0) {
    datasets.push({
      label: "Filing Dates",
      data: filingDates.datasets[0].data,
      borderColor: "rgb(255, 206, 86)", // Yellow
      backgroundColor: "rgba(255, 206, 86, 0.1)",
    });
  }

  // Add final action dates dataset
  if (finalActionDates.datasets && finalActionDates.datasets.length > 0) {
    datasets.push({
      label: "Final Action Dates",
      data: finalActionDates.datasets[0].data,
      borderColor: "rgb(75, 192, 75)", // Green
      backgroundColor: "rgba(75, 192, 75, 0.1)",
    });
  }

  return {
    labels: labels,
    datasets: datasets,
  };
}

/**
 * Get common chart options configuration
 * @param {string} title - Chart title
 * @param {Array} dataValues - Array of date values to calculate min/max
 * @param {Array} octoberIndices - Array of indices where October months occur
 * @returns {Object} Chart.js options object
 */
function getChartOptions(title, dataValues, octoberIndices = []) {
  // Calculate min and max dates from the data
  const numericDates = dataValues
    .filter((v) => v !== "C" && v !== "U" && v !== null && v !== undefined)
    .map((v) => new Date(v).getTime())
    .filter((t) => !isNaN(t));

  let minDate, maxDate;
  if (numericDates.length > 0) {
    const minTime = Math.min(...numericDates);
    const maxTime = Math.max(...numericDates);

    // Subtract/add one year (in milliseconds)
    const oneYear = 365.25 * 24 * 60 * 60 * 1000;
    minDate = new Date(minTime - oneYear);
    maxDate = new Date(maxTime + oneYear);
  }

  // Check if small screen for responsive display
  const isSmallScreen = window.innerWidth < 480;

  return {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2,
    interaction: {
      mode: "index",
      intersect: false,
    },
    // Animation: Points appear progressively from left to right
    animation: {
      onComplete: () => {},
      delay: (context) => {
        let delay = 0;
        if (context.type === "data" && context.mode === "default") {
          delay = context.dataIndex * 20;
        }
        return delay;
      },
    },
    // Accessibility: Enable keyboard navigation
    events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"],
    plugins: {
      octoberLines: {
        octoberIndices: octoberIndices,
      },
      title: {
        display: false,
      },
      legend: {
        display: true,
        position: "top",
        onClick: (e, legendItem, legend) => {
          // Toggle dataset visibility
          const index = legendItem.datasetIndex;
          const chart = legend.chart;
          const meta = chart.getDatasetMeta(index);

          meta.hidden =
            meta.hidden === null ? !chart.data.datasets[index].hidden : null;
          chart.update();
        },
        onHover: (e) => {
          e.native.target.style.cursor = "pointer";
        },
        onLeave: (e) => {
          e.native.target.style.cursor = "default";
        },
        labels: {
          usePointStyle: true,
          padding: 15,
          // Accessibility: Add focus styling
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return datasets.map((dataset, i) => ({
              text: dataset.label,
              fillStyle: dataset.borderColor,
              hidden: !chart.isDatasetVisible(i),
              lineCap: dataset.borderCapStyle,
              lineDash: dataset.borderDash,
              lineDashOffset: dataset.borderDashOffset,
              lineJoin: dataset.borderJoinStyle,
              lineWidth: dataset.borderWidth,
              strokeStyle: dataset.borderColor,
              pointStyle: dataset.pointStyle,
              datasetIndex: i,
            }));
          },
        },
      },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        callbacks: {
          title: function (tooltipItems) {
            // Show the time period (bulletin month)
            return tooltipItems[0].label;
          },
          label: function (context) {
            // Show the exact date value
            const label = context.dataset.label || "";
            const value = context.parsed.y;

            // Format the date value for display
            if (value === "C") {
              return `${label}: Current`;
            } else if (value === "U") {
              return `${label}: Unavailable`;
            } else {
              return `${label}: ${formatDateForDisplay(value)}`;
            }
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "BULLETIN MONTH",
          font: {
            size: 14,
          },
        },
        ticks: {
          display: !isSmallScreen,
          maxRotation: isSmallScreen ? 90 : 45,
          minRotation: isSmallScreen ? 90 : 45,
        },
      },
      y: {
        title: {
          display: true,
          text: "PRIORITY DATE",
          font: {
            size: 14,
          },
        },
        type: "time",
        time: {
          unit: "year",
          displayFormats: {
            year: "yyyy",
          },
          tooltipFormat: "MMM dd, yyyy",
        },
        min: minDate,
        max: maxDate,
        ticks: {
          display: !isSmallScreen,
          callback: function (value) {
            // Format Y-axis labels to show only year
            const date = new Date(value);
            return date.getFullYear();
          },
        },
      },
    },
  };
}

/**
 * Format date value for display
 * @param {string|number} value - Date value to format
 * @returns {string} Formatted date string
 */
function formatDateForDisplay(value) {
  if (value === "C") return "Current";
  if (value === "U") return "Unavailable";

  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }

    const options = { year: "numeric", month: "short", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  } catch (error) {
    return value;
  }
}

/**
 * Display error message in chart container
 * @param {string} canvasId - ID of the canvas element
 * @param {string} message - Error message to display
 */
function showChartError(canvasId, message) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;

  const container = canvas.parentElement;
  if (!container) return;

  // Hide the canvas
  canvas.style.display = "none";

  // Create error message element
  const errorDiv = document.createElement("div");
  errorDiv.className = "chart-error";
  errorDiv.setAttribute("role", "alert");
  errorDiv.textContent = message;
  errorDiv.style.cssText = `
    padding: 20px;
    margin: 10px 0;
    background-color: #fee;
    border: 1px solid #fcc;
    border-radius: 4px;
    color: #c33;
    text-align: center;
  `;

  container.appendChild(errorDiv);
}

/**
 * Add window resize listener to update chart responsively
 * @param {Chart} chart - The Chart.js instance
 */
function addResizeListener(chart) {
  let resizeTimeout;

  const handleResize = () => {
    // Clear any existing timeout
    clearTimeout(resizeTimeout);

    // Debounce resize to avoid excessive updates
    resizeTimeout = setTimeout(() => {
      if (chart && !chart.destroyed) {
        // Update responsive point sizes
        const isSmallScreen = window.innerWidth < 480;
        const pointRadius = isSmallScreen ? 2 : 4;
        const pointHoverRadius = isSmallScreen ? 4 : 6;

        // Update all datasets with new point sizes
        chart.data.datasets.forEach((dataset) => {
          dataset.pointRadius = pointRadius;
          dataset.pointHoverRadius = pointHoverRadius;
        });

        // Update tick display based on screen size
        chart.options.scales.x.ticks.display = !isSmallScreen;
        chart.options.scales.y.ticks.display = !isSmallScreen;

        // Trigger chart resize and update
        chart.resize();
        chart.update("none"); // 'none' mode for instant update without animation
      }
    }, 250); // 250ms debounce delay
  };

  window.addEventListener("resize", handleResize);

  // Store cleanup function on chart instance for potential future cleanup
  chart._resizeHandler = handleResize;
}

/**
 * Add keyboard navigation support to chart canvas
 * @param {HTMLCanvasElement} canvas - The canvas element
 * @param {Chart} chart - The Chart.js instance
 */
function addKeyboardNavigation(canvas, chart) {
  let currentDataIndex = 0;

  canvas.addEventListener("keydown", (event) => {
    const dataLength = chart.data.labels.length;

    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        currentDataIndex = Math.min(currentDataIndex + 1, dataLength - 1);
        highlightDataPoint(chart, currentDataIndex);
        announceDataPoint(chart, currentDataIndex);
        break;

      case "ArrowLeft":
        event.preventDefault();
        currentDataIndex = Math.max(currentDataIndex - 1, 0);
        highlightDataPoint(chart, currentDataIndex);
        announceDataPoint(chart, currentDataIndex);
        break;

      case "Home":
        event.preventDefault();
        currentDataIndex = 0;
        highlightDataPoint(chart, currentDataIndex);
        announceDataPoint(chart, currentDataIndex);
        break;

      case "End":
        event.preventDefault();
        currentDataIndex = dataLength - 1;
        highlightDataPoint(chart, currentDataIndex);
        announceDataPoint(chart, currentDataIndex);
        break;
    }
  });

  // Reset on blur
  canvas.addEventListener("blur", () => {
    currentDataIndex = 0;
  });
}

/**
 * Highlight a specific data point on the chart
 * @param {Chart} chart - The Chart.js instance
 * @param {number} index - The data point index to highlight
 */
function highlightDataPoint(chart, index) {
  // Update tooltip to show all datasets at the selected data point
  const activeElements = chart.data.datasets.map((_, datasetIndex) => ({
    datasetIndex,
    index,
  }));
  chart.tooltip.setActiveElements(activeElements);
  chart.update();
}

/**
 * Announce data point to screen readers
 * @param {Chart} chart - The Chart.js instance
 * @param {number} index - The data point index
 */
function announceDataPoint(chart, index) {
  const label = chart.data.labels[index];

  // Create or update live region for screen reader announcements
  let liveRegion = document.getElementById("chart-live-region");
  if (!liveRegion) {
    liveRegion = document.createElement("div");
    liveRegion.id = "chart-live-region";
    liveRegion.setAttribute("role", "status");
    liveRegion.setAttribute("aria-live", "polite");
    liveRegion.setAttribute("aria-atomic", "true");
    liveRegion.className = "visually-hidden";
    document.body.appendChild(liveRegion);
  }

  // Announce all datasets at this index
  const announcements = chart.data.datasets.map((dataset) => {
    const value = dataset.data[index];
    const formattedValue = formatDateForDisplay(value);
    return `${dataset.label}: ${formattedValue}`;
  });

  liveRegion.textContent = `${label}. ${announcements.join(", ")}`;
}

// Make functions available globally for inline script usage
if (typeof window !== "undefined") {
  window.initializeCharts = initializeCharts;
  window.initializeCombinedChart = initializeCombinedChart;
}
